import { Alignment, Api, Currency, CurrencySettings, DateUtil, ModelRow } from "@mcleod/core";
import { Layout, SlideoutDecorator, TableRow, TableRowDisplayEvent } from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { FilterPanel } from "@mcleod/dispatch";
import { PayablesLookup } from "../OrderPayables";
import { OrderPayablesUtil } from "../OrderPayablesUtil";
import { ManageRecurringPayAdjustmentProps, ManageRecurringPayAdjustments } from "../ManageRecurringPayAdjustments";
import { OrderPayablesTaskFilter } from "../OrderPayablesTaskFilter";
import { DriverSettlementTabLayout } from "./DriverSettlement";
import { AutogenLayoutDriverSettlementProcess } from "./autogen/AutogenLayoutDriverSettlementProcess";
import { OrderPayablesProcessStepper } from "../OrderPayablesProcessStepper";
import { DriverSettlementFilter } from "./DriverSettlementFilter";
import { RowDriverSettlementProfile } from "../../driver-settlement/src/models/ModelDriverSettlementProfile";
import { SettlementPayeeDataLookup } from "../PayeeData";


export class DriverSettlementProcess extends AutogenLayoutDriverSettlementProcess implements DriverSettlementTabLayout {
    public filterPanel: FilterPanel;
    public getProfileRow: () => RowDriverSettlementProfile;
    public getPayablesLookupMap: () => PayablesLookup;
    public getPayeeLookupMap: () => SettlementPayeeDataLookup;
    public selectedGroupByRows: TableRow[] = [];
    public selectedGroupByValue: string = "";
    public payrollCutoffDate: Date;
    public recurringDate: Date; // this date is used so that recurrings always use the earlier of payroll or profile cutoff
    public allGroupByRows: () => TableRow[];

    onLoad(): void | Promise<void> {
        this.filterPanel = new FilterPanel([OrderPayablesTaskFilter.SUMMARY_PROCESS, OrderPayablesTaskFilter.PAYMENT_PROCESS], this.tableDriverSettlement, this.panelTaskFilters);
        this.tableDriverSettlement.addRowDisplayListener((event: TableRowDisplayEvent) => this.tableOrderPayablesOnRowDisplay(event));
    }

    getLayoutBaseFilter(row: ModelRow): boolean {
        return row.get("ready_to_pay_flag") == "Y";
    }

    tableOrderPayablesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        tableRow.forEveryChildComponent((component) => {
            OrderPayablesUtil.setTooltipContent(component, tableRow.data);
        });
        OrderPayablesUtil.setComponentDetails({
            tableRow: tableRow,
            profileRow: this.getProfileRow()
        });
    }

    public resetProcessButtonCaption() {
        const selectedRows = this.tableDriverSettlement.selectedRows;
        this.buttonProcess.caption = "Process (" + selectedRows.length + ")";
    }

    async driverSettlementProcess() {
        this.buttonProcess.busy = true;
        const shouldContinue = await this.checkRecurringPayAdjustments();
        if (!shouldContinue) {
            this.buttonProcess.busy = false;
            return;
        }

        const profileRow = this.getProfileRow();
        const readyToPayPayables = this.getReadyToPaySettlementsAndAdjustments();

        // Calculate settlement total
        const settlementTotal = new Currency({ currency_code: profileRow.get("currency_type"), amount: 0 });

        const payeeIdSet = new Set<string>();
        const payeeTypeSet = new Set<string>();
        const checkNumbersSet = new Set<string>();

        readyToPayPayables.forEach(row => {
            settlementTotal.add(row.getCurrency("total_pay"));
            if (row.get("check_number") !== undefined) {
                checkNumbersSet.add(row.get("check_number"));
            }
        });

        const acceptedPayeeRows = this.allGroupByRows().filter((row: TableRow) => !row.data.isNull("process_status"));
        if (acceptedPayeeRows.length === 0) {
            CommonDialogs.showError("No payees have been accepted for payment. Please review and try again.", "No Payees Accepted");
            this.buttonProcess.busy = false;
            return;
        }
        acceptedPayeeRows.forEach(row => {
            payeeIdSet.add(row.data.get("payee_id"));
            const payeeData = this.getPayeeLookupMap().getPayeeData(row.data.get("payee_id"));
            payeeTypeSet.add(payeeData.getPayeeRow().get("payee_type"));
        });

        const payeeIdArray = Array.from(payeeIdSet);
        const payeeTypeArray = Array.from(payeeTypeSet);
        const checkNumbersArray = Array.from(checkNumbersSet);

        const selectedSettlements = readyToPayPayables.filter(row => row.get("record_type") === "S");
        const settlementAdjustments = this.getAdjustmentIdsFromSettlements(selectedSettlements);
        const deductions = readyToPayPayables.filter(row => row.get("record_type") === "P").map(row => row.get("record_id")).concat(settlementAdjustments);

        const totalPay = new Currency({
            currency_code: profileRow.get("currency_type"),
            amount: readyToPayPayables.reduce((sum, row: ModelRow) => sum + row.getCurrency("pay_amount", 0).getAmount(), 0)
        });

        const processCount = this.getProcessCount(readyToPayPayables);
        const payeeCount = payeeIdArray.length;

        // Determine cutoff date: use the earlier of profile and payroll dates, or payroll date if profile is not set
        const profileCutoffDate =  DateUtil.parseDateWithKeywords(profileRow.get("cutoff_date"), true, false);
        const cutoffDate = profileCutoffDate ?? this.payrollCutoffDate;

        OrderPayablesProcessStepper.showAsSlideout({
            title: `Process Drivers (${payeeCount})`,
            settlementIds: selectedSettlements.map(row => row.get("record_id")).join(","),
            deductionIds: deductions.join(","),
            checkNumbers: checkNumbersArray.join(","),
            checkNumberCount: checkNumbersArray.length,
            cutoffDate: cutoffDate,
            recurringDate: this.recurringDate,
            endDate: cutoffDate,
            payeeIds: payeeIdArray.join(","),
            payeeTypes: payeeTypeArray.join(","),
            profileRow,
            processCount,
            selectedPayables: readyToPayPayables,
            total: totalPay,
            isDriverSettlement: true,
            payrollCutoffDate: this.payrollCutoffDate,
            displayRecordSelectionDates: true
        }).finally(() => {
            this.buttonProcess.busy = false;
        });
    }

    private async checkRecurringPayAdjustments(): Promise<boolean> {
        const response = await Api.search("lme/settlement/manage-recurring-pay-adjustments", this.getManageRecurringProps());
        if (response?.data?.length > 0) {
            const isYes = await CommonDialogs.showYesNo("There are recurring pay adjustments that have not been processed for this pay period. Would you like to process them now?", "Manage Recurring Pay Adjustments");
            if (isYes) {
                this.buttonManageRecurringOnClick();
                return Promise.resolve(false);
            } else if (isYes === undefined) {
                return Promise.resolve(false);
            }
        }
        return Promise.resolve(true);
    }

    private getManageRecurringProps(): ManageRecurringPayAdjustmentProps {
        const overrideCutoffDate = DateUtil.parseDateWithKeywords(this.getProfileRow().get("cutoff_date"));

        // Use payroll cutoff if override cutoff is null, otherwise use the earlier date
        let cutoffDate: Date;
        if (overrideCutoffDate == null) {
            cutoffDate = this.payrollCutoffDate ?? new Date();
        } else {
            cutoffDate = this.payrollCutoffDate && overrideCutoffDate > this.payrollCutoffDate
                ? this.payrollCutoffDate
                : overrideCutoffDate;
        }

        return {
            date: cutoffDate,
            currency_code: this.getProfileRow().get("currency_type"),
            payee_type: this.getProfileRow().get("payee_type"),
            payee_status: this.getProfileRow().get("payee_status"),
            payees: this.getProfileRow().get("payee_ids"),
            payees_exclude: this.getProfileRow().getBoolean("payee_ids_exclude", false),
            departments: this.getProfileRow().get("departments"),
            departments_exclude: this.getProfileRow().getBoolean("departments_exclude", false),
            frozen_payables: this.getProfileRow().get("frozen_payables"),
            payment_method: this.getProfileRow().get("payment_method"),
            bonus_code: this.getProfileRow().get("bonus_code"),
            bonus_tax_method: this.getProfileRow().get("bonus_tax_method"),
            override_frequency: this.getProfileRow().get("override_frequency"),
            is_driver_settlement: true,
            showDisabledTooltip: false
        }
    }

    buttonManageRecurringOnClick() {
        const layout = Layout.getLayout("lme/settlement/ManageRecurringPayAdjustments") as ManageRecurringPayAdjustments;
        SlideoutDecorator.decorateAndSlideIn(layout, {
            width: "100%",
            fillVerticalSpace: true,
            title: "Manage Recurring Pay Adjustments",
            transitionOptions: {
                direction: Alignment.BOTTOM
            },
            layoutLoadListeners: () => {
                layout.mainDataSource.orderBy = [{ field: "payee_id", sort: "asc" }];
                layout.props = this.getManageRecurringProps();
                layout.buttonSearch.visible = false;
                layout.textboxDate.text = DateUtil.formatDate(layout.props.date);
                layout.textboxDate.enabled = false;
                layout.buttonSearch.clicked();
            }
        });
    }

    getReadyToPaySettlementsAndAdjustments(): ModelRow[] {
        const filter = new DriverSettlementFilter(this.getProfileRow());
        const records: ModelRow[] = [];
        for (const row of this.sourceOrderPayablesCache.data) {
            if (CurrencySettings.getSingleton().multiCurrencyEnabled() && row.getCurrency("pay_amount").currency_code !== this.getProfileRow().get("currency_type")) {
                continue; // Skip rows with different currency
            }
            if (filter.dataPasses(row) && !row.isNull("payee_process_status") && row.get("ready_to_pay_flag") == "Y") {
                records.push(row);
            }
        }
        return records;
    }

    getAdjustmentIdsFromSettlements(settlements: ModelRow[]): String[] {
        const adjustments: ModelRow[] = [];
        for (const settlement of settlements) {
            OrderPayablesUtil.getRelatedPayAdjustmentRows(settlement, this.getPayablesLookupMap()).forEach((adjustment) => {
                const passesGroupBy = this.selectedGroupByRows.some((tableRow: TableRow) => tableRow.data.get("group_by_value") == adjustment.get(this.selectedGroupByValue));
                const rolledUp = OrderPayablesUtil.settlementInTabWithAdjustment(adjustment, this.getPayablesLookupMap());
                const isProcessStatus = adjustment.get("ready_to_pay_flag") == "Y";
                if (passesGroupBy && rolledUp && isProcessStatus) {
                    adjustments.push(adjustment);
                }
            });
        }
        return adjustments.map(row => row.get("record_id"));
    }

    getProcessCount(payables: ModelRow[]): number {
        return payables.filter(row => {
            if (CurrencySettings.getSingleton().multiCurrencyEnabled() && row.getCurrency("pay_amount").currency_code !== this.getProfileRow().get("currency_type")) {
                return false; // Skip rows with different currency
            }
            if (row.get("record_type") === "P") {
                return !OrderPayablesUtil.settlementInTabWithAdjustment(row, this.getPayablesLookupMap());
            }
            return true;
        }).length;
    }
}
